export const getYouTubeVideoId = (url: string): string | null => {
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.toLowerCase();

    if (host.includes('youtu.be')) {
      return parsed.pathname.split('/').filter(Boolean)[0] ?? null;
    }

    if (host.includes('youtube.com')) {
      const direct = parsed.searchParams.get('v');
      if (direct) return direct;

      const segments = parsed.pathname.split('/').filter(Boolean);
      const embedIndex = segments.indexOf('embed');
      if (embedIndex >= 0 && segments[embedIndex + 1]) return segments[embedIndex + 1];

      const shortsIndex = segments.indexOf('shorts');
      if (shortsIndex >= 0 && segments[shortsIndex + 1]) return segments[shortsIndex + 1];
    }
  } catch {
    return null;
  }

  return null;
};

const getVimeoVideoId = (url: string): string | null => {
  try {
    const parsed = new URL(url);
    if (!parsed.hostname.toLowerCase().includes('vimeo.com')) return null;
    const segments = parsed.pathname.split('/').filter(Boolean);
    const id = [...segments].reverse().find((segment) => /^\d+$/.test(segment));
    return id ?? null;
  } catch {
    return null;
  }
};

const getDailymotionVideoId = (url: string): string | null => {
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.toLowerCase();
    const segments = parsed.pathname.split('/').filter(Boolean);

    if (host.includes('dai.ly')) return segments[0] ?? null;
    if (host.includes('dailymotion.com')) {
      const videoIndex = segments.indexOf('video');
      if (videoIndex >= 0 && segments[videoIndex + 1]) return segments[videoIndex + 1];
    }
  } catch {
    return null;
  }
  return null;
};

const getStreamableVideoId = (url: string): string | null => {
  try {
    const parsed = new URL(url);
    if (!parsed.hostname.toLowerCase().includes('streamable.com')) return null;
    const segments = parsed.pathname.split('/').filter(Boolean);
    return segments[segments.length - 1] ?? null;
  } catch {
    return null;
  }
};

const getWistiaVideoId = (url: string): string | null => {
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.toLowerCase();
    if (!host.includes('wistia')) return null;
    const segments = parsed.pathname.split('/').filter(Boolean);
    const mediasIndex = segments.indexOf('medias');
    if (mediasIndex >= 0 && segments[mediasIndex + 1]) return segments[mediasIndex + 1];
    return segments[segments.length - 1] ?? null;
  } catch {
    return null;
  }
};

export const getVideoEmbedUrl = (url: string): string | null => {
  const youtubeId = getYouTubeVideoId(url);
  if (youtubeId) return `https://www.youtube.com/embed/${youtubeId}?rel=0`;

  const vimeoId = getVimeoVideoId(url);
  if (vimeoId) return `https://player.vimeo.com/video/${vimeoId}`;

  const dailyMotionId = getDailymotionVideoId(url);
  if (dailyMotionId) return `https://www.dailymotion.com/embed/video/${dailyMotionId}`;

  const streamableId = getStreamableVideoId(url);
  if (streamableId) return `https://streamable.com/e/${streamableId}`;

  const lowerUrl = url.toLowerCase();
  if (lowerUrl.includes('soundcloud.com')) {
    return `https://w.soundcloud.com/player/?url=${encodeURIComponent(url)}&auto_play=false&show_comments=false&show_teaser=true&visual=true`;
  }

  if (lowerUrl.includes('facebook.com') || lowerUrl.includes('fb.watch')) {
    return `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(url)}&show_text=false`;
  }

  if (lowerUrl.includes('twitch.tv')) {
    const parent =
      typeof window !== 'undefined' && window.location.hostname
        ? window.location.hostname
        : 'localhost';
    try {
      const parsed = new URL(url);
      const segments = parsed.pathname.split('/').filter(Boolean);
      if (parsed.hostname.toLowerCase().includes('clips.twitch.tv') && segments[0]) {
        return `https://clips.twitch.tv/embed?clip=${segments[0]}&parent=${parent}`;
      }
      const videosIndex = segments.indexOf('videos');
      if (videosIndex >= 0 && segments[videosIndex + 1]) {
        return `https://player.twitch.tv/?video=v${segments[videosIndex + 1]}&parent=${parent}`;
      }
      if (segments[0]) {
        return `https://player.twitch.tv/?channel=${segments[0]}&parent=${parent}`;
      }
    } catch {
      return null;
    }
  }

  const wistiaId = getWistiaVideoId(url);
  if (wistiaId) return `https://fast.wistia.net/embed/iframe/${wistiaId}`;

  return null;
};

export const getVideoThumbnailUrl = (url: string): string | null => {
  const youtubeId = getYouTubeVideoId(url);
  if (youtubeId) return `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`;

  const vimeoId = getVimeoVideoId(url);
  if (vimeoId) return `https://vumbnail.com/${vimeoId}.jpg`;

  const dailyMotionId = getDailymotionVideoId(url);
  if (dailyMotionId) return `https://www.dailymotion.com/thumbnail/video/${dailyMotionId}`;

  const streamableId = getStreamableVideoId(url);
  if (streamableId) return `https://cdn-cf-east.streamable.com/image/${streamableId}.jpg`;

  const wistiaId = getWistiaVideoId(url);
  if (wistiaId) return `https://fast.wistia.com/embed/medias/${wistiaId}/swatch`;

  return null;
};
